import 'react-perfect-scrollbar/dist/css/styles.css';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from "styled-components"
import GlobalStyles from './components/GlobalStyles';
import theme from './theme';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';

import { ScansProvider } from "./contexts/ScansContext";
import { PopupNotifProvider } from "./contexts/PopupNotifContext";

import DashboardLayout from './components/DashboardLayout';
import MainLayout from './components/MainLayout';

import Account from './pages/Account';
import Scans from './pages/Scans'
import ViewScan from './pages/ViewScan'
import ViewScanUsers from './pages/ViewScanUsers'
import CreateScan from './pages/CreateScan'

import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import ValidateCode from './pages/ValidateCode';
import RegisterCandidat from './pages/RegisterCandidat'
import Applicants from './pages/Applicants'   // S8
import Home from './pages/Home';
import NotFound from './pages/NotFound';


import { isLoggedIn, getCurrentUser } from "./utils/auth"

function RequireAuthAdmin({ children }: { children: JSX.Element }) {
  let location = useLocation();
  if (!isLoggedIn()) {
    return <Navigate to="/login" state={{ from: location }} />;
  }
  // Un candidat n'a rien a faire sur les pages recruteur : sans ce garde-fou,
  // /app/scan/:id ou /app/applicants etaient atteignables a l'URL.
  //
  // /app/account fait exception : c'est SON compte, pas une page recruteur.
  // La premiere version de ce garde ne laissait passer que /app/scans et
  // enfermait donc le candidat hors de son mot de passe et de sa langue.
  const PAGES_CANDIDAT = ["/app/scans", "/app/account"]
  if (getCurrentUser()?.type === "theScanCandidat" && !PAGES_CANDIDAT.includes(location.pathname)) {
    return <Navigate to="/app/scans" state={{ from: location }} />;
  }
  return (
    // S8 : ScansProvider remonte au-dessus du layout — la barre du haut
    // (recherche globale) consomme les scans, elle est rendue par le layout.
    <ScansProvider>
      <DashboardLayout>
        {children}
      </DashboardLayout>
    </ScansProvider>
  );
}

function GoToHome({ children }: { children: JSX.Element }) {
  let location = useLocation();
  if (isLoggedIn()) {
    return <Navigate to="/app/scans" state={{ from: location }} />;
  } else {
    return <Navigate to="/home" state={{ from: location }} />;
  }
}

function GoTo404({ children }: { children: JSX.Element }) {
  let location = useLocation();
  return <Navigate to="/404" state={{ from: location }} />;
}

function AlwaysLoginRedirect({ children }: { children: JSX.Element }) {
  let location = useLocation();
  if (isLoggedIn()) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  return children;
}

const App = () => {
  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <PopupNotifProvider>
            <GlobalStyles />
            <Routes>
              <Route exact path='/home' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <Home/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/login' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <Login/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/sign-up' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <SignUp/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/forgotPassword' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <ForgotPassword/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/resetPassword' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <ResetPassword/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/validateCode' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <ValidateCode/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/registerCandidat' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <RegisterCandidat/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
              <Route exact path='/404' element={
                <MainLayout>
                  <NotFound/>
                </MainLayout>
              }/>


              <Route exact path="/" element={
                <GoToHome/>
              }/>
              <Route exact path="/app/scan" element={
                <GoToHome/>
              }/>
              <Route exact path="/app/scan/:id" element={
                <RequireAuthAdmin>
                  <ViewScan />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/scan/:id/invitees" element={
                <RequireAuthAdmin>
                  <ViewScanUsers />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/scan/:id/completed" element={
                <RequireAuthAdmin>
                  <ViewScanUsers />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/scan/:id/passed" element={
                <RequireAuthAdmin>
                  <ViewScanUsers />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/scans" element={
                <RequireAuthAdmin>
                  <Scans />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/applicants" element={
                <RequireAuthAdmin>
                  <Applicants />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/createScan" element={
                <RequireAuthAdmin>
                  <CreateScan />
                </RequireAuthAdmin>
              }/>
              <Route exact path="/app/account" element={
                <RequireAuthAdmin>
                  <Account />
                </RequireAuthAdmin>
              }/>

              <Route exact path='*' element={
                <GoTo404/>
              }/>
            </Routes>
          </PopupNotifProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
